/**
 * iter 376 — Site-wide "Teacher of the Month" celebration banner.
 *
 * Any authenticated user hits `/api/teacher-of-month/active-celebration` on
 * mount. If a winner was declared in the last 72 h and the current user has
 * not dismissed it yet (localStorage flag), we render a soft golden banner
 * with a trophy and fire a burst of confetti. Persists across route changes
 * because it's mounted at the App root.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Trophy, X, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HIDDEN_PATHS = ['/login', '/auth/callback'];

function fireConfetti() {
  const duration = 2.5 * 1000;
  const end = Date.now() + duration;
  const colors = ['#f59e0b', '#fbbf24', '#f97316', '#eab308'];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  }());
}

export default function TeacherOfMonthCelebration() {
  const [winner, setWinner] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Skip on the login pages
    if (HIDDEN_PATHS.some(p => window.location.pathname.startsWith(p))) return;
    axios.get(`${API_URL}/api/teacher-of-month/active-celebration`, { withCredentials: true })
      .then(r => {
        const w = r.data?.winner;
        if (!w) return;
        const key = `tom_celebration_dismissed_${w.month}_${w.declared_at}`;
        if (window.localStorage.getItem(key)) { setDismissed(true); return; }
        setWinner(w);
        // Fire confetti on first paint per session
        const sessionKey = `tom_celebration_fired_${w.month}_${w.declared_at}`;
        if (!window.sessionStorage.getItem(sessionKey)) {
          try { fireConfetti(); } catch {}
          window.sessionStorage.setItem(sessionKey, '1');
        }
      })
      .catch(() => {});
  }, []);

  if (!winner || dismissed) return null;

  const dismiss = () => {
    const key = `tom_celebration_dismissed_${winner.month}_${winner.declared_at}`;
    window.localStorage.setItem(key, '1');
    setDismissed(true);
  };

  return (
    <div
      className="fixed top-16 left-1/2 -translate-x-1/2 z-50 max-w-2xl w-[90vw] rounded-xl shadow-2xl border border-amber-400/60 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-rose-500/20 backdrop-blur-sm text-white flex items-center gap-3 p-3"
      data-testid="tom-celebration-banner"
      role="status"
    >
      <div className="w-11 h-11 rounded-full bg-amber-400/30 flex items-center justify-center shrink-0 ring-2 ring-amber-400/50">
        <Trophy className="w-6 h-6 text-amber-300" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-amber-200 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Teacher of the Month · {winner.month}
        </p>
        <p className="text-white font-semibold truncate">
          🎉 Congratulations, {winner.teacher_name}!
        </p>
        <p className="text-slate-200 text-xs">
          Total score {winner.total_score?.toFixed(1)} · Declared by {winner.declared_by_name || 'admin'}
        </p>
      </div>
      <button
        onClick={dismiss}
        className="text-slate-300 hover:text-white p-1"
        data-testid="tom-celebration-dismiss"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
